'use client'

import NavLink from '@/components/ui/NavLink'
import AppIcon from '@/components/content/app/AppIcon'
import Badge from '@/components/ui/Badge'
import { Placeholder } from '@/components/ui/Placeholder'
import { MenuTitle } from '@/components/navigation/SubMenu'
import { Icon } from '@/icons/Icon'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { checkAppBranding } from '@/lib/formatting'
import { createLogger } from '@/lib/logger'
import { getBrandColors } from '@/lib/utils'
import { getDealTags } from '@/components/content/deals/helpers'
import { useDealsMenuContext } from '@/context/NavbarContext'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import type { App, Deal, Category } from '@/payload-types'

const logger = createLogger('DealsMenu')

type DealLinkProps = {
  deal: Deal
}

export default function DealsMenu() {
  const { deals = [], categories = [], categoryCounts = {} } = useDealsMenuContext()

  const dealsToDisplay = determineDealsToDisplay(deals)

  return (
    <div
      data-menu="Deals"
      className="flex w-full shrink-0 flex-col divide-x divide-gray-200 dark:divide-white/10 sm:flex-row navigation:w-[680px]"
    >
      <div className="flex w-full flex-col gap-2 px-2 py-5 sm:w-1/2">
        <MenuTitle title="Featured Deals" />
        <div className="flex flex-col navigation:gap-3">
          {dealsToDisplay.length > 0
            ? dealsToDisplay.slice(0, 5).map((deal, index) => {
                if (deal.app) return <DealLink key={index} deal={deal} />
              })
            : Array.from({ length: 5 }, (_, index) => <Placeholder className="h-16" key={index} />)}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 px-2 pb-5 pt-2 sm:w-1/2 sm:pt-5 md:pb-2">
        <MenuTitle title="By Category" />
        {categories.length > 0
          ? categories.map((category, index) => (
              <CategoryLink
                key={index}
                category={category}
                count={categoryCounts[category.id] ?? 0}
              />
            ))
          : Array.from({ length: 7 }, (_, index) => <Placeholder className="h-10" key={index} />)}

        <NavLink
          href="/deals#all"
          className="group hidden h-14 items-center gap-2 px-5 text-13 text-gray-500 transition-colors hover:text-gray-950 dark:hover:text-white md:flex"
        >
          All deals by category
          <div className="rotate-180 transition-colors">
            <Icon name="chevron-left" size={12} />
          </div>
        </NavLink>
      </div>
    </div>
  )
}

export function DealLink({ deal }: DealLinkProps) {
  const { name } = deal
  const app = typeof deal.app === 'object' && deal.app !== null ? (deal.app as App) : null

  if (!app) {
    logger.error('Deal app is undefined in DealLink', { dealId: deal.id })
    return null
  }

  const totalDeals = app.deals?.docs?.length ?? 1
  const hasMultipleDeals = totalDeals > 1

  const link = `/deals/${app.slug}`

  const tags = getDealTags(deal)

  const storeDealID = (id: string) => {
    localStorage.setItem('efficient:deal_id', id)
  }

  const { appIcon, appGlyph } = checkAppBranding(app)
  const hasIcon = Boolean(appIcon || appGlyph)
  const { backgroundColor } = getBrandColors(app)

  return (
    <NavLink
      href={link}
      className="group flex items-center gap-5 rounded-xl px-5 py-3 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
      onClick={() => {
        trackFeaturedClick(FeaturedPlacement.NAV_DEALS, app.name)
        if (hasMultipleDeals) {
          storeDealID(deal.id.toString())
        }
      }}
    >
      {hasIcon && (
        <AppIcon
          icon={appIcon}
          glyph={appGlyph}
          name={app.name}
          background={backgroundColor}
          size={40}
        />
      )}
      <div className="flex h-10 w-full -translate-y-0.5 flex-col gap-1 truncate md:-translate-y-px md:gap-1.5">
        <div className="flex items-center gap-2">
          <span className="flex h-5 items-center whitespace-nowrap text-14 font-medium leading-none text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:text-white">
            {app.name}
          </span>
          {tags.new && (
            <Badge style="secondary" nested>
              New!
            </Badge>
          )}
          {tags.daysRemaining && (
            <Badge
              count={`${tags.daysRemaining}d`}
              className="cursor-pointer"
              icon="circle-half-dotted-clock"
              nested
            />
          )}
        </div>
        <span className="w-full truncate whitespace-nowrap pb-2 text-12 leading-none text-zinc-500">
          {name}
        </span>
      </div>
    </NavLink>
  )
}

export function CategoryLink({ category, count }: { category: Category; count: number }) {
  if (!category) {
    logger.error('Category is undefined in CategoryLink')
    return null
  }

  return (
    <NavLink
      href={`/deals#${category.slug}`}
      className="group flex h-10 items-center gap-5 rounded-xl px-5 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-[0.75px] border-black/10 bg-white text-primary-500 dark:border-white/10 dark:bg-black dark:text-white">
        {category.icon && <Icon name={category.icon} size={12} strokeWidth={1} />}
      </div>
      <span className="w-full text-13 font-medium text-gray-950 transition-colors duration-300 dark:text-white">
        {category.name}
      </span>
      <Badge count={count} icon="deal" nested />
    </NavLink>
  )
}
